import type { ReactNode } from 'react';
import { AirplaneTakeoffIcon, CalendarBlankIcon } from '@phosphor-icons/react';
import { formatDateRange, parseDateOnly } from '@voll-intelligence/dates';
import { PersonAvatar } from '../../shared/person/person_row.js';
import { LegChain, legChainText } from '../../shared/summary_pill/leg_chain.js';
import {
  SummaryPill,
  type SummaryPillSegment,
  type SummaryPillTarget,
} from '../../shared/summary_pill/summary_pill.js';

/**
 * A pill de resumo da busca de VOO, a MESMA na bancada do web-chat e no
 * explorer da conversa: forma da viagem, viajante, rota e datas; a lupa
 * re-busca o MESMO pedido. Quem monta traduz o pedido dele em `FlightSummary`
 * e diz o que o clique faz (os dois expandem o planner no campo do segmento).
 *
 * A rota BINÁRIA é um segmento só, "GRU → REC" (#3239 C4): em 883px duas
 * pílulas com cidade saíam cortadas; as cidades (`cityOf`) ficam no nome
 * acessível e no hover. O MULTI é uma pílula de duas linhas — a cadeia de
 * praças em cima, "N trechos · janela" embaixo — e não tem segmento de datas:
 * a janela já mora na pílula.
 */

export type FlightTripType = 'one_way' | 'round_trip' | 'multi_leg';

export type FlightSummaryLeg = { origin: string; destination: string; departDate: string };

export type FlightSummary = {
  tripType: FlightTripType;
  legs: readonly FlightSummaryLeg[];
  /** `YYYY-MM-DD`; só vale no `round_trip`. */
  returnDate?: string;
  /** Quantos viajam. Ausente (o explorer não conhece o pedido de pessoas), a
   *  segunda linha do celular fica sem a contagem. */
  travelers?: number;
  /** O primeiro viajante, com o nome curto que a pill mostra. */
  lead?: { name: string; photoUrl?: string | null };
};

export type FlightSummaryPillCopy = {
  aria: string;
  tripType: Readonly<Record<FlightTripType, string>>;
  /** "2 trechos" — a linha de baixo da pílula do multi. */
  legs: (count: number) => string;
  /** "2 viajantes" — a segunda linha do celular, quando há `travelers`. */
  travelers?: (count: number) => string;
};

export type FlightSummaryPillProps = {
  summary: FlightSummary;
  copy: FlightSummaryPillCopy;
  locale: string;
  /** A cidade de um IATA, para o nome acessível da rota ("GRU São Paulo").
   *  Sem cidade, só o IATA. */
  cityOf?: (iata: string) => string | null | undefined;
  /** Planner expandido: a pill se esconde (visibility) mas fica montada. */
  hidden?: boolean;
  readOnly?: boolean;
  onExpand?: (target: SummaryPillTarget) => void;
  /** A lupa e o nome dela, como na `SummaryPill`. Ausente ⇒ sem lupa. */
  search?: { onSearch: () => void; label: string };
};

/** A janela da viagem: do primeiro embarque à volta (ida e volta) ou ao último
 *  embarque — "17 jun", "20–22 jun". Vazia sem ida válida; fim inválido ou no
 *  mesmo dia vira um dia só. */
function tripWindow(summary: FlightSummary, locale: string): string {
  const start = parseDateOnly(summary.legs[0]?.departDate);
  if (!start) return '';
  const end = parseDateOnly(
    summary.tripType === 'round_trip' ? summary.returnDate : summary.legs.at(-1)?.departDate,
  );
  return formatDateRange(start, end, locale, new Date());
}

function routeSegment(
  summary: FlightSummary,
  window: string,
  copy: FlightSummaryPillCopy,
  place: (iata: string) => string,
): SummaryPillSegment {
  if (summary.tripType !== 'multi_leg') {
    const first = summary.legs.slice(0, 1);
    return {
      key: 'route',
      strong: true,
      icon: <AirplaneTakeoffIcon size={14} aria-hidden />,
      label: <LegChain legs={first} />,
      ariaLabel: legChainText(first, place),
    };
  }
  const sub = [copy.legs(summary.legs.length), window].filter(Boolean).join(' · ');
  return {
    key: 'route',
    strong: true,
    label: <LegChain legs={summary.legs} />,
    caption: sub,
    ariaLabel: `${legChainText(summary.legs, place)} · ${sub}`,
  };
}

function travelerSegments(summary: FlightSummary, locale: string): SummaryPillSegment[] {
  const { lead } = summary;
  if (!lead) return [];
  const others = (summary.travelers ?? 1) - 1;
  return [
    {
      key: 'traveler',
      icon: (
        <PersonAvatar name={lead.name} photoUrl={lead.photoUrl ?? null} size="xs" locale={locale} />
      ),
      label: others > 0 ? `${lead.name} +${String(others)}` : lead.name,
    },
  ];
}

/** As duas linhas do celular (#2660): a cadeia de praças + a janela em cima; a
 *  forma + quantos viajam embaixo. No MULTI a cadeia já diz a forma, e a janela
 *  desce no lugar dela: a cadeia com quebra é longa, e a data na mesma linha
 *  truncava primeiro. */
function compactLines(
  summary: FlightSummary,
  window: string,
  copy: FlightSummaryPillCopy,
): { primary: string; secondary: string } {
  const chain = legChainText(summary.legs);
  const travelers =
    summary.travelers !== undefined && copy.travelers ? copy.travelers(summary.travelers) : '';
  if (summary.tripType === 'multi_leg') {
    return { primary: chain, secondary: [window, travelers].filter(Boolean).join(' · ') };
  }
  return {
    primary: [chain, window].filter(Boolean).join(' · '),
    secondary: [copy.tripType[summary.tripType], travelers].filter(Boolean).join(' · '),
  };
}

/** A pill, ou nada sem trecho. */
export function FlightSummaryPill({
  summary,
  copy,
  locale,
  cityOf,
  hidden,
  readOnly,
  onExpand,
  search,
}: Readonly<FlightSummaryPillProps>): ReactNode {
  if (summary.legs.length === 0) return null;
  const place = (iata: string): string => {
    const city = cityOf?.(iata);
    return city ? `${iata} ${city}` : iata;
  };
  const window = tripWindow(summary, locale);
  const dated = window && summary.tripType !== 'multi_leg';
  const segments: SummaryPillSegment[] = [
    { key: 'trip_type', label: copy.tripType[summary.tripType] },
    ...travelerSegments(summary, locale),
    routeSegment(summary, window, copy, place),
    ...(dated
      ? [
          {
            key: 'dates' as const,
            icon: <CalendarBlankIcon size={14} aria-hidden />,
            label: window,
          },
        ]
      : []),
  ];
  return (
    <SummaryPill
      segments={segments}
      compact={compactLines(summary, window, copy)}
      ariaLabel={copy.aria}
      {...(hidden !== undefined ? { hidden } : {})}
      {...(readOnly !== undefined ? { readOnly } : {})}
      {...(onExpand ? { onExpand } : {})}
      {...(search ? { search } : {})}
    />
  );
}
